import { Repeat, CalendarDays, CalendarCheck, Flag, Hash, CalendarClock } from "lucide-react";

const ACCENT = "#D99B1E";
const GREEN = "#2A9A7A";
const DAYS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
const FREQ = { hourly: "Horaire", daily: "Quotidienne", weekly: "Hebdomadaire", monthly: "Mensuelle", quarterly: "Trimestrielle", semiannual: "Semestrielle", yearly: "Annuelle", custom: "Date personnalisée" };

const fmtShort = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" });
const fmtLong = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

const relative = (iso) => {
  const days = Math.round((new Date(iso).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86400000);
  if (days === 0) return "aujourd'hui";
  if (days === 1) return "demain";
  return `dans ${days} jours`;
};

const dayCell = (rec) => {
  switch (rec.frequency) {
    case "monthly": return ["Jour du mois", rec.day_of_month ? `le ${rec.day_of_month}` : "même jour que la tâche"];
    case "weekly": return ["Jour de la semaine", rec.weekdays?.length ? `chaque ${rec.weekdays.map((d) => DAYS[d]).join(", ")}` : "chaque semaine"];
    case "daily": return ["Jour", "tous les jours"];
    case "hourly": return ["Rythme", "toutes les heures"];
    case "quarterly": return ["Rythme", "tous les 3 mois"];
    case "custom": return ["Rythme", `toutes les ${rec.custom_hours || 1} h`];
    default: return ["Jour", "—"];
  }
};

const endCell = (rec) => {
  if (rec.end_date) return `jusqu'au ${fmtShort(rec.end_date)}`;
  if (rec.max_occurrences) return `après ${rec.max_occurrences} occurrence${rec.max_occurrences > 1 ? "s" : ""}`;
  return "Aucune";
};

function Cell({ Icon, label, value, tid }) {
  return (
    <div data-testid={tid}>
      <div className="flex items-center" style={{ gap: 4, fontSize: 9, color: "#8b8b93", letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 3 }}><Icon size={10} />{label}</div>
      <div style={{ fontSize: 13, fontWeight: 500, color: "#e8e8ec" }}>{value}</div>
    </div>
  );
}

const INDEX_TYPE = { month: "Mois", year: "Année", deadline: "Date d'échéance", custom: "Liste perso" };
export default function RecurrenceBlock({ recurrence, indice }) {
  if (!recurrence) return null;
  const rec = recurrence;
  const [dayLabel, dayValue] = dayCell(rec);
  const stopped = !rec.active && !!rec.stopped_at && !rec.next_occurrence;
  const finished = !stopped && !rec.next_occurrence;
  return (
    <div style={{ background: "#141418", border: "0.5px solid #26262a", borderLeft: `3px solid ${ACCENT}`, borderRadius: 8, padding: "14px 18px" }} data-testid="td-recurrence-block">
      <div className="flex items-center" style={{ gap: 6, fontSize: 10, color: "#8b8b93", letterSpacing: ".08em", marginBottom: 12 }}><Repeat size={12} style={{ color: ACCENT }} />RÉCURRENCE</div>
      <div className="grid grid-cols-2 md:grid-cols-4" style={{ gap: 14 }}>
        <Cell Icon={Repeat} label="Fréquence" value={FREQ[rec.frequency] || rec.frequency} tid="td-rec-frequency" />
        <Cell Icon={CalendarDays} label={dayLabel} value={dayValue} tid="td-rec-day" />
        <Cell Icon={Flag} label="Fin" value={endCell(rec)} tid="td-rec-end" />
        <Cell Icon={Hash} label="Générées" value={rec.occurrences_count || 0} tid="td-rec-generated" />
        {rec.index_type && <Cell Icon={Hash} label={`Indice · ${INDEX_TYPE[rec.index_type] || rec.index_type}`} value={indice || "—"} tid="td-indice" />}
      </div>
      {rec.next_occurrence ? (
        <div className="flex items-center" style={{ gap: 10, marginTop: 14, background: "#12201c", border: `0.5px solid ${GREEN}`, borderRadius: 8, padding: "10px 14px" }} data-testid="td-rec-next">
          <CalendarClock size={18} style={{ color: GREEN, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 10, color: "#8b8b93", letterSpacing: ".08em" }}>PROCHAINE OCCURRENCE</div>
            <div style={{ fontSize: 13, fontWeight: 500, color: GREEN }} data-testid="td-rec-next-date">{fmtLong(rec.next_occurrence)} · {relative(rec.next_occurrence)}</div>
          </div>
        </div>
      ) : (
        <div className="flex items-center" style={{ gap: 8, marginTop: 14, fontSize: 12, color: "#8b8b93" }} data-testid="td-rec-ended">
          <CalendarCheck size={14} />{stopped ? "Récurrence arrêtée" : finished ? "Récurrence terminée" : ""}
        </div>
      )}
    </div>
  );
}
